/**
 * Responsibility: describe the rolling lakeland biome — gentle open ground with small lakes on it.
 *
 * The middle of three lake biomes. Glacial `lakeland` has the same water on bumpy, rocky-reading ground; `woodedLakes` has
 * this ground with trees on it. Each pair differs in exactly one way, which is what makes them tell apart from the air —
 * and it is why all three exist rather than one biome with a wide tuning range, which would average out into neither.
 *
 * Everything is shared: the terrain comes from `lakeBasins.js`, the surface and shore from `waterSurface.js`. What is left
 * is a config block.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createLakeTerrain } from '../lakeBasins.js';
import { createWaterSurface, createWaterClearance } from '../waterSurface.js';
import { ROLLING_LAKES, PALETTE } from '../../config.js';

export const rollingLakes = {
  id: 'rollingLakes',
  label: 'Rolling lakeland',

  palette: PALETTE.rollingLakes,

  suitabilityBias: ROLLING_LAKES.SUITABILITY_BIAS,

  /**
   * Build the elevation function for this biome.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number} Elevation in world units, always >= 0.
   */
  createElevation(seed) {
    return createLakeTerrain(seed, 'rollingLakes', ROLLING_LAKES).elevation;
  },

  /** Highest elevation this biome can produce, in world units. */
  maxElevation: ROLLING_LAKES.BASE_HEIGHT + ROLLING_LAKES.BASE_RELIEF + ROLLING_LAKES.GROUND_RELIEF,

  /**
   * Build the structure placement function: the lake surfaces.
   *
   * @param {number} seed - Base world seed.
   * @returns {(region: object, context: object) => Array<object>}
   */
  createStructures(seed) {
    const terrain = createLakeTerrain(seed, 'rollingLakes', ROLLING_LAKES);

    return createWaterSurface({
      ownDepthAt: terrain.ownDepth,

      // Flat by construction: the base field only, with none of this biome's ground detail in it.
      waterLevelAt: terrain.waterLevel,
      couldHoldWater: terrain.insideBasin,
      grid: ROLLING_LAKES.WATER_GRID,
      maxDepth: ROLLING_LAKES.DEPTH - ROLLING_LAKES.WATER_DROP,
    });
  },

  /**
   * Publish the lakes so nothing stands in them.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number, radius: number, context: object) => boolean}
   */
  createReservation(seed) {
    const terrain = createLakeTerrain(seed, 'rollingLakes', ROLLING_LAKES);

    return createWaterClearance({
      ownDepthAt: terrain.ownDepth,
      nearWater: terrain.nearBasin,
    });
  },
};
